import { useCallback, useEffect, useRef } from "react";
import {
  useBeforeUnload,
  useBlocker,
  useLocation,
  type Location,
  type BlockerFunction,
} from "react-router-dom";
import {
  hasContextDraft,
  requestContextLeave,
} from "@/stores/contextNavigation";
type Props = {
  consumePermit: (target: string) => boolean;
  hasPending: (target?: string) => boolean;
  /** journeyId：目标历史项带的暂存 id（后退/前进回到当前暂存的设置页时原页会停放，不卸载）。 */
  requestLeave: (target: string, journeyId?: string | null) => Promise<boolean>;
};
const href = (location: Location) =>
  location.pathname + location.search + location.hash;
const journeyIdOf = (location: Location): string | null => {
  const id = (location.state as { quoteJourneyId?: unknown } | null)
    ?.quoteJourneyId;
  return typeof id === "string" ? id : null;
};
/** 单个全局阻断器；受控提交同步消费许可，普通导航/POP保持首次待决目标。 */
export function QuoteJourneyNavigation({
  consumePermit,
  hasPending,
  requestLeave,
}: Props) {
  const location = useLocation(),
    latest = useRef(location),
    alive = useRef(false);
  latest.current = location;
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ nextLocation }) =>
        hasContextDraft() ||
        (!consumePermit(href(nextLocation)) && hasPending(href(nextLocation))),
      [consumePermit, hasPending],
    ),
  );
  const pending = useRef<object | null>(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      pending.current = null;
    };
  }, []);
  useEffect(() => {
    if (blocker.state !== "blocked" || pending.current) return;
    const attempt = {},
      key = latest.current.key,
      first = blocker;
    pending.current = attempt;
    void requestLeave(href(first.location), journeyIdOf(first.location))
      .then((ok) => ok && requestContextLeave())
      .then((ok) => {
        if (
          !alive.current ||
          pending.current !== attempt ||
          latest.current.key !== key
        )
          return;
        if (ok) first.proceed();
        else first.reset();
      })
      .finally(() => {
        if (pending.current === attempt) pending.current = null;
      });
  }, [blocker, requestLeave]);
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!hasPending() && !hasContextDraft()) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [hasPending],
    ),
  );
  return null;
}
